"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, BookOpenCheck, UserCheck, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const pillars = [{
  id: "multi-carrier",
  icon: ShieldCheck,
  title: "Multi-carrier access",
  description: "Because we work independently rather than representing a single insurance carrier, we compare products across top-rated providers to find the terms and features that fit your specific goals.",
  highlight: "Objective market comparisons"
}, {
  id: "education-first",
  icon: BookOpenCheck,
  title: "Education over pressure",
  description: "We break down complex insurance and retirement vehicles into plain language. You get the clarity needed to understand surrender schedules, caps, and guarantees at your own pace.",
  highlight: "Zero sales quotas or rushed choices"
}, {
  id: "tailored-solutions",
  icon: UserCheck,
  title: "Tailored to your circumstances",
  description: "Every household has distinct income targets, health baselines, and legacy goals. We evaluate product availability and underwriting requirements specifically for your situation.",
  highlight: "Customized suitability reviews"
}];
const standards = ["Licensed independent professionals", "Transparent product terms & limitations", "Clear carrier ratings & disclosures", "No obligation consultations"];
export default function Approach() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-approach" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="bg-muted text-foreground border-border px-3 py-1 font-semibold uppercase tracking-wider text-xs rounded-full">
              Our advisory approach
            </Badge>
          </div>

          <h2 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.18] mb-6">
            Education-first guidance across independent carriers
          </h2>

          {/* Architectural Anchor Dot Rule Motif */}
          <div className="flex items-center gap-2 mb-6">
            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
            <div className="h-px w-24 sm:w-36 bg-border" />
          </div>

          <p className="font-sans text-lg sm:text-xl text-muted-foreground leading-relaxed">
            We believe you deserve clear, objective advice. Our consultations focus entirely on your family's personal
            circumstances and long-term security, rather than sales quotas or aggressive pitches.
          </p>
        </motion.div>

        {/* Core Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12 lg:mb-16">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.id} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full bg-card border border-border rounded-xl p-6 sm:p-8 flex flex-col justify-between shadow-sm transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md">
                  <div>
                    <div className="w-12 h-12 rounded-lg bg-muted text-primary flex items-center justify-center mb-6">
                      <Icon className="w-6 h-6" strokeWidth={1.75} />
                    </div>
                    <CardHeader className="p-0 mb-4">
                      <CardTitle className="font-default text-xl sm:text-2xl font-semibold text-card-foreground">
                        {pillar.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <p className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {pillar.description}
                      </p>
                    </CardContent>
                  </div>
                  <div className="mt-8 pt-4 border-t border-border flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-xs font-medium text-muted-foreground">
                      {pillar.highlight}
                    </span>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Regulatory & Compliance Clarity Callout */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 lg:gap-10">
            <div className="space-y-3 max-w-2xl">
              <div className="flex items-center gap-2.5">
                <FileText className="w-5 h-5 text-primary shrink-0" strokeWidth={1.75} />
                <h3 className="font-default text-lg sm:text-xl font-semibold text-foreground">
                  Our professional advisory standard
                </h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                DJTD Consulting operates as an independent insurance and financial services advisory practice. All recommendations are subject to individual carrier underwriting, state availability, and comprehensive client suitability assessments.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                {standards.map((standard, idx) => <div key={idx} data-index={idx} className="flex items-center gap-2 text-xs font-medium text-foreground/90">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span>{standard}</span>
                  </div>)}
              </div>
            </div>

            <div className="w-full sm:w-auto shrink-0">
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-md">
                <Link to="/FAQKnowledgeCenterPage">
                  <span>Explore learning center</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}